import { scopedApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ErrorResponse } from '@/types/endpoints';
import type { TrackingInfo } from '@/types/order';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../requestOptions';

export type OrderReturnBulkItem = {
  quantity: number;
  order_item_id: number;
  carrier?: string;
  return_tracking?: TrackingInfo[];
};

export type MysqlWarning = { level: string; message: string };

// The generic bulk create answers 201 with `num_results` for the rows it inserted, and lists MySQL warnings for any
// rows it ignored or altered, so a success can still carry `insert_errors`.
export type OrderReturnBulkResponse =
  | {
      isError?: false;
      result: null;
      errors?: { insert_errors: MysqlWarning[] | null } | null;
      status: number;
      execution_time: number;
      num_results: number;
    }
  | ErrorResponse;

type RequestParams = {
  orderId: number | string;
  data: OrderReturnBulkItem[];
};

export const postOrderReturnBulk = async (
  scope: RequestScope,
  { orderId, data }: RequestParams,
  options: WriteRequestOptions
): Promise<OrderReturnBulkResponse> => {
  try {
    if (!orderId && orderId !== 0) {
      throw new Error('`orderId` is required');
    }
    if (!Array.isArray(data) || data.length < 1) {
      throw new Error('`data` must be a non-empty array');
    }

    // Backend expects the array payload directly (not wrapped in `{ data: ... }`)
    const response = await scopedApi(scope).post<OrderReturnBulkResponse>(`order/${orderId}/return/bulk`, data, options);
    return response.data;
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
